// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TySubscription } from "@/types";
import { formatMoney, readLangValue } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import TextInput from "@/UI/inputs/TextInput";
// --- Types --------------------------------------------------------------------------------------
export type TyConfirmPaymentDetails = { subscription: TySubscription; onDone: () => void };

type Props = { onClose: () => void; details?: unknown };
// --- Logic --------------------------------------------------------------------------------------
/** `yyyy-mm-dd`, which is the only thing a native date input accepts or returns. */
const today = (): string => new Date().toISOString().slice(0, 10);

/**
 * The money arrived. This is a **form rather than a yes/no**, and that is the whole point: nothing
 * else in the system records that a transfer happened, so the date it landed, what actually landed and
 * the reference that finds it on a statement are the only evidence there will ever be.
 *
 * Confirming is what grants access and sets the period's dates — see the `confirm` controller for
 * where it starts, which depends on whether the company already has a live period.
 */
export default function ConfirmPaymentPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const { subscription, onDone } = (details ?? {}) as TyConfirmPaymentDetails;

    const name = readLangValue(subscription.organization.name, lang) || subscription.organization.slug;

    const [paidAt, setPaidAt] = useState(today());
    const [price, setPrice] = useState(String(subscription.price));
    const [receipt, setReceipt] = useState("");
    const [note, setNote] = useState(subscription.note ?? "");
    const [formError, setFormError] = useState<string | null>(null);

    const save = useFetch(`/subscriptions/${subscription._id}/confirm`, { method: "POST" }, false);

    useEffect(() => {
        if (!save.succeededAt) return;
        onDone?.();
        onClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [save.succeededAt]);

    const handleSubmit = () => {
        setFormError(null);
        if (!paidAt) return setFormError(t.subscriptions.fields.paidAt);

        const amount = Number(price);
        if (!Number.isFinite(amount) || amount < 0) return setFormError(t.subscriptions.fields.amountReceived);

        save.invoke({ method: "POST", data: { paidAt, price: amount, receipt: receipt.trim() || null, note: note.trim() || null } });
    };

    return (
        <section className="side-form side-form--center">
            <header className="side-form__header">
                <h2 className="side-form__title">{t.subscriptions.confirmTitle.replace("{name}", name)}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                <div className="side-form__facts">
                    <div className="side-form__fact">
                        <span>{t.subscriptions.fields.period}</span>
                        <strong>{t.subscriptions.periods[subscription.period]}</strong>
                    </div>
                    <div className="side-form__fact">
                        <span>{t.subscriptions.fields.price}</span>
                        <strong>
                            {formatMoney(subscription.price)} {t.subscriptions.currency}
                        </strong>
                    </div>
                </div>

                <TextInput value={paidAt} onChange={setPaidAt} label={t.subscriptions.fields.paidAt} type="date" dir="ltr" disabled={save.loading} />
                <TextInput
                    value={price}
                    onChange={setPrice}
                    label={`${t.subscriptions.fields.amountReceived} · ${t.subscriptions.currency}`}
                    dir="ltr"
                    disabled={save.loading}
                />
                <TextInput value={receipt} onChange={setReceipt} label={t.subscriptions.fields.receipt} dir="ltr" disabled={save.loading} />
                <p className="side-form__hint">{t.subscriptions.receiptHint}</p>

                <TextInput value={note} onChange={setNote} label={t.subscriptions.fields.note} multiline rows={2} disabled={save.loading} />
                <p className="side-form__hint">{t.subscriptions.confirmHint}</p>

                {(formError || save.error) && <p className="side-form__error">{formError ?? save.error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={save.loading}>
                    {t.common.cancel}
                </button>
                <button type="button" className="side-form__submit" onClick={handleSubmit} disabled={save.loading}>
                    {save.loading ? t.common.loading : t.subscriptions.confirmPayment}
                </button>
            </footer>
        </section>
    );
}
